import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { MsalService } from '@azure/msal-angular';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';
import { EncryptDecryptService } from './encrypt-decrypt.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ConfirmationService } from 'primeng/api';
import { Constants, appConfig } from '../models/constants';
@Injectable({ providedIn: 'root' })
export class StorageService {
    isAdmin: boolean = false;
    isFunctional: boolean = false;
    isHomeAllowed: boolean = false;
    isTaxAdjustmentAdmin: boolean = false;
    isCoaAdminSelected: boolean = false;
    retryCount: number = 0;
    retry: boolean = false;
    private readonly cookieStorageKeys: string[];
    // Only these local-cache keys are session scoped; all other keys stay in localStorage.
    private readonly sessionScopedLocalCacheKeys: Set<string>;
    concurrFound = new BehaviorSubject(false);
    concurrFound$ = this.concurrFound.asObservable();
    private retrySubject = new Subject<void>();
    retry$ = this.retrySubject.asObservable();
    loggedIn = new BehaviorSubject(false);
    loggedIn$ = this.loggedIn.asObservable();
    // True while MSAL is starting up or completing a login, so the Sign In page is not shown behind the loader.
    authInProgress = new BehaviorSubject(false);
    authInProgress$ = this.authInProgress.asObservable();
    isExternal = false;
    constructor(
        private readonly cookieService: CookieService,
        private authService: MsalService,
        private msalService: MsalService,
        private router: Router,
        private encryptDecryptService: EncryptDecryptService,
        private httpClient: HttpClient,
        private confirmationService: ConfirmationService,
    ) {
        this.cookieStorageKeys = ['TOKEN'];
        // Migrated keys: role context should not persist across browser sessions.
        this.sessionScopedLocalCacheKeys = new Set(['selectedRole', 'userRoleData']);
    }

    triggerRetry() {
        this.retrySubject.next();
    }

    setTokenInBackend() {
        return this.httpClient.get(Constants.GATEWAY_BASE_API_URL + '/refresh/token', { responseType: 'json' });
    }

    setCookies(idToken:any, key='msal.idtoken') {
        let time = this.createTokenForOneHour();
        this.cookieService.set(key, idToken, time, '/', undefined, false, 'Strict');
        this.cookieService.set(key, idToken, time, '/', undefined, true, 'None');
        // this.getLocalStorageToSession();
    }

    setSessionStorage(key, value) {
        sessionStorage.setItem(key, value);
    }

    getSessionStorage(key) {
        return sessionStorage.getItem(key);
    }

    getCookie(key) {
        return this.cookieService.get(key);
    }
    
    clearAll(): void {
        localStorage.clear();
        sessionStorage.clear();
        this.cookieService.deleteAll();
    }

    getLocalStorageToSession() {
        const items = { ...localStorage };
        var json_str = JSON.stringify(items);
        sessionStorage.setItem('localStorageData', json_str);
        this.clearTokenfromLocal();
    }

    unAuthUserFound() {
        this.clearAll();
        this.msalPostLogoutRedirect();
    }

    logOut(concurrent : boolean) {
        this.httpClient.get(Constants.GATEWAY_BASE_API_URL + '/logout/' + concurrent, { responseType: 'json'}).subscribe(() => { });
        this.isExternal = localStorage.getItem('src') === 'external';
        if(!concurrent)
            this.clearAll();
        this.msalPostLogoutRedirect();
    }

    msalPostLogoutRedirect() {
        const options = this.isExternal ? { postLogoutRedirectUri: Constants.postLogoutRedirectURI } : undefined;
        this.msalService.instance.logoutRedirect(options);
    }

    public get(key: string): string {
        //encrypt added
        if (!key) {
            return 'undefined';
        }
        key = key.includes('Global_') ? key.replace('Global_', '') : key;
        const encValue = sessionStorage.getItem('Global_' + key);
        if (!encValue) {
            return 'undefined';
        }
        const decVal = this.encryptDecryptService.decrypt(encValue);
        return decVal;
    }

    public set(key: string, value: string) {
        let time = this.createTokenForOneHour();
        //new code with encrypt
        if (!key || !value) {
            return;
        }
        const encValue = this.encryptDecryptService.encrypt(value);
        sessionStorage.setItem('Global_' + key, encValue);
    }

    public setWithDefaultPassphrase(key: string, value: string) {
        let time = this.createTokenForOneHour();
        //new code with encrypt
        if (!key || !value) {
            return;
        }
        const encValue = this.encryptDecryptService.encryptWithDefault(value);
        sessionStorage.setItem('Global_' + key, encValue);
    }

    public getWithDefaultPassphrase(key: string): string {
        //encrypt added
        if (!key) {
            return 'undefined';
        }
        key = key.includes('Global_') ? key.replace('Global_', '') : key;
        const encValue = sessionStorage.getItem('Global_' + key);
        if (!encValue) {
            return 'undefined';
        }
        const decVal = this.encryptDecryptService.decryptWithDefault(encValue);
        return decVal;
    }

    public removeItem(key: string) {
        key = key.includes('Global_') ? key : 'Global_' + key;
        sessionStorage.removeItem(key);
    }

    clearTokenfromLocal() {
        let localData = { ...localStorage };
        for (const property in localData) {
            if (property.includes('msal') || property.includes('{')) {
                localStorage.removeItem(property);
            }
        }
        this.refreshTokenTimer();
    }

    refreshTokenTimer() {
        const scopes = ['user.read', 'openid', 'Mail.Send'];
        const renewIdTokenRequest = {
            clientId: appConfig.appConfigService?.azureAdConfig?.clientId,
            scopes: scopes,
            account: this.authService.instance.getActiveAccount()
        };
        setTimeout(() => this.renewToken(renewIdTokenRequest), 1800000);
    }

    renewToken(renewIdTokenRequest: any) {
        this.authService.instance.acquireTokenSilent(renewIdTokenRequest).then(
            (response) => {
                this.setCookies(response.idToken);
                this.retry = true;
            }
        ).catch((error) => {
            // MSAL v5 error messages are doc links, so rely on errorCode to detect blocked popups.
            if (['popup_window_error', 'empty_window_error'].includes(error?.errorCode) || error?.message?.includes('popups')) {
                this.confirmationService.confirm({
                    message:
                        'Please always allow popups from DigiCorporateTax portal for continuing your work without getting logged out. This setting is usually available under Privacy and security section in browser settings.',
                    acceptLabel: 'Ok',
                    rejectVisible: false,
                    accept: () => {
                        this.confirmationService.close();
                    },
                });
            }
            setTimeout(() => {
                this.authService.instance.acquireTokenSilent(renewIdTokenRequest).then((response) => {
                    this.setCookies(response.idToken);
                }).catch((error) => {
                    console.log('refresh token error:: ', error);
                    this.errorFoundInToken();
                });
            }, 10000);
        });
    }

    errorFoundInToken() {
        if (
            ((!localStorage.getItem('msal.idtoken') || !this.get('msal.idtoken') || this.cookieService.get('msal.idtoken')) &&
                !this.tokenValidateTime) ||
            this.retryCount > 10
        ) {
            this.unAuthUserFound();
        } else {
            const renewIdTokenRequest = {
                clientId: appConfig.appConfigService?.azureAdConfig?.clientId,
                scopes: ['openid'],
                forceRefresh: true,
            };
            this.renewToken(renewIdTokenRequest);
        }
        this.retryCount++;
    }

    rewnalCookiesTime() {
        let cookiesData = this.cookieService.getAll();
        for (const property in cookiesData) {
            if (property.includes('Global_')) {
                this.cookieService.delete(property);
                this.set(property, cookiesData[property]);
            }
        }
    }
    createTokenForOneHour() {
        return new Date(Date.now() + 5*60*60 * 1000);
    }

    getUserName() {
        return (this.authService.instance?.getActiveAccount()?.name.length > 0 ? this.authService?.instance?.getActiveAccount()?.name : this.deCodeJWT('name')) || '';
    }

    getUserEmail() {
        return (
            (this.authService?.instance?.getActiveAccount()?.username.length > 0
                ? this.authService?.instance?.getActiveAccount()?.username.toLowerCase()
                : this.deCodeJWT('preferred_username')) || ''
        );
    }

    deCodeJWT(key: any) {
        let token = this.cookieService.get('msal.idtoken');
        var decoded: any = jwtDecode(token);
        return decoded[key];
    }

    tokenValidateTime() {
        return (new Date().getTime() <= Number(this.cookieService.get('msal.idTokenExpiry')));
        // const jwtToken = this.cookieService.get('msal.idtoken');
        // if (!jwtToken) {
        //     return false;
        // }
        // const parsedToken = JSON.parse(atob(jwtToken.split('.')[1]));
        // return Date.now() <= parsedToken.exp * 1000;
    }

    private privacy = new BehaviorSubject(null);
    privacyAccepted$ = this.privacy.asObservable();

    changePrivacy(value: any) {
        this.privacy.next(value);
    }

    clearSessionAgaintCountry(){
        let cookies = this.cookieService.getAll();
        for (let cookie in cookies) {
            if(cookie !== 'msal.idtoken' && cookie !== 'msal.idTokenExpiry')
            {
                this.cookieService.delete(cookie, '/', undefined, false, 'Strict');
                this.cookieService.delete(cookie, '/', undefined, true, 'None');
            }
        }
    }

    public setLocalCache(key: string, value: string) {
        if (!value) {
            return;
        }
        const encValue = this.encryptDecryptService.encrypt(value);
        // Writes selectedRole/userRoleData to sessionStorage, all others to localStorage.
        this.getLocalCacheStorage(key).setItem(key, encValue);
    }

    public getLocalCache(key: string) {
        if (!key) {
            return;
        }
        // Reads from the same storage target selected by key routing.
        const storage = this.getLocalCacheStorage(key);
        const cachedValue = storage.getItem(key);
        if(cachedValue) {
            const decVal = this.encryptDecryptService.decrypt(cachedValue);
            return decVal;
        } else 
            return;
    }
    public removeLocalCache(key: string) {
       this.getLocalCacheStorage(key).removeItem(key);
    }

    private getLocalCacheStorage(key: string): Storage {
        // Centralized routing avoids changing dozens of callers.
        return this.sessionScopedLocalCacheKeys.has(key) ? sessionStorage : localStorage;
    }

    public encodeValue(key: string, value: string) {
        let encoded = this.encryptDecryptService.encode(value);
        sessionStorage.setItem('Global_' + key, encoded);
    }

    public decodeValue(key: string) {
        if (!key) {
            return 'undefined';
        }
        key = key.includes('Global_') ? key.replace('Global_', '') : key;
        const encValue = sessionStorage.getItem('Global_' + key);
        if (!encValue) {
            return 'undefined';
        }
        const decVal = this.encryptDecryptService.decode(encValue);
        return decVal;
    }

    endPreviousSession(){
        return this.httpClient.get(Constants.GATEWAY_BASE_API_URL + '/endPreSession', { responseType: 'json' });
    }
}
